import * as React from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { useAppTranslation } from '../../services/i18n';
import { SettingNested, SettingRow, SettingsCard } from './SettingRow';
import { mandatoryParamsPatch, SettingsTabProps } from './settingsTab';

const DOMAIN_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;

export const isValidDomain = (value: string) => DOMAIN_PATTERN.test(value);

export const RegistrationSettings = ({ settings, isOwner, onSave }: SettingsTabProps) => {
  const t = useAppTranslation();
  const mandatoryParams = settings.mandatory_params;
  const domains = settings.allowed_domains || [];
  const isJobTitleRequired = !!mandatoryParams?.job_title;

  const handleDomainsChange = (value: string[]) => {
    const next = Array.from(new Set(value.map((domain) => domain.trim().toLowerCase())));
    const saved = domains.map((domain) => domain.toLowerCase());
    // only a newly typed value is checked, so a saved domain the pattern rejects stays removable
    if (next.some((domain) => !saved.includes(domain) && !isValidDomain(domain))) {
      return;
    }
    onSave({ allowed_domains: next });
  };

  return (
    <>
      <SettingsCard title={t('companySettings.registration.emailDomainTitle')}>
        <SettingRow
          label={t('companySettings.registration.domainCheck.label')}
          helper={t('companySettings.registration.domainCheck.helper')}
          info={{
            title: t('companySettings.registration.domainCheck.infoTitle'),
            text: t('companySettings.registration.domainCheck.infoText'),
          }}
          checked={settings.domain_check}
          disabled={!isOwner}
          onChange={(value) => onSave({ domain_check: value, allowed_domains: domains })}
        />
        <div className={'setting-row__extra'}>
          <Autocomplete
            multiple
            freeSolo
            autoSelect
            size={'small'}
            options={[] as string[]}
            value={domains}
            disabled={!isOwner || !settings.domain_check}
            onChange={(event, value) => handleDomainsChange(value as string[])}
            renderValue={(value, getItemProps) =>
              value.map((domain, index) => (
                <Chip size={'small'} label={domain} {...getItemProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant={'standard'}
                slotProps={{
                  ...params.slotProps,
                  htmlInput: {
                    ...params.slotProps.htmlInput,
                    'aria-label': t('companySettings.registration.allowedDomains.label'),
                  },
                }}
                placeholder={t(
                  domains.length
                    ? 'companySettings.registration.allowedDomains.addAnother'
                    : 'companySettings.registration.allowedDomains.placeholder'
                )}
              />
            )}
          />
        </div>
        <SettingRow
          label={t('companySettings.registration.domainHardCheck.label')}
          helper={
            settings.domain_check
              ? t('companySettings.registration.domainHardCheck.helper')
              : t('companySettings.registration.domainHardCheck.helperDisabled')
          }
          checked={settings.domain_hard_check}
          disabled={!isOwner || !settings.domain_check}
          onChange={(value) => onSave({ domain_hard_check: value, allowed_domains: domains })}
        />
      </SettingsCard>

      <SettingsCard
        title={t('companySettings.registration.mandatoryTitle')}
        subtitle={t('companySettings.registration.mandatorySubtitle')}
      >
        <SettingRow
          label={t('companySettings.registration.personalId.label')}
          checked={!!mandatoryParams?.personal_id}
          disabled={!isOwner}
          onChange={(value) => onSave(mandatoryParamsPatch(settings, 'personal_id', value))}
        />
        <SettingRow
          label={t('companySettings.registration.birthDate.label')}
          checked={!!mandatoryParams?.birth_date}
          disabled={!isOwner}
          onChange={(value) => onSave(mandatoryParamsPatch(settings, 'birth_date', value))}
        />
        <SettingRow
          label={t('companySettings.registration.jobTitle.label')}
          checked={isJobTitleRequired}
          disabled={!isOwner}
          onChange={(value) => onSave(mandatoryParamsPatch(settings, 'job_title', value))}
        />
        <SettingNested>
          <SettingRow
            label={t('companySettings.registration.allowJobTitle.label')}
            helper={
              isJobTitleRequired
                ? undefined
                : t('companySettings.registration.allowJobTitle.helperDisabled')
            }
            checked={settings.user_allowed_to_add_job_title}
            disabled={!isOwner || !isJobTitleRequired}
            onChange={(value) => onSave({ user_allowed_to_add_job_title: value })}
          />
        </SettingNested>
      </SettingsCard>

      <SettingsCard title={t('companySettings.registration.loginTitle')}>
        <SettingRow
          label={t('companySettings.registration.emailCheck.label')}
          helper={t('companySettings.registration.emailCheck.helper')}
          checked={settings.email_hard_check}
          disabled={!isOwner}
          onChange={(value) => onSave({ email_hard_check: value })}
        />
        <SettingRow
          label={t('companySettings.registration.passwordCheck.label')}
          helper={t('companySettings.registration.passwordCheck.helper')}
          checked={settings.password_check}
          disabled={!isOwner}
          onChange={(value) => onSave({ password_check: value })}
        />
      </SettingsCard>
    </>
  );
};
